import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/site/Reveal";
import { IMAGES, API } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Newsletter = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      toast.error(t("newsletter.invalid"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setDone(true);
      toast.success(t("newsletter.success"));
      setEmail("");
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      const msg = err?.response?.status === 409
        ? err.response.data.detail
        : t("newsletter.error");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="relative bg-[#F8F9FA] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="relative overflow-hidden bg-white border border-[#0A1128]/10 shadow-sm">
          <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block">
            <img src={IMAGES.fbla4} alt="ICDH students" className="w-full h-full object-cover object-[center_25%]" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent" />
          </div>
          <div className="relative z-10 px-8 md:px-16 py-16 md:py-24 max-w-2xl">
            <Reveal>
              <div className="flex items-center gap-3 text-[#B8901F] mb-5">
                <Mail size={18} />
                <Overline>{t("newsletter.overline")}</Overline>
              </div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#0A1128] leading-[1.05]">
                {t("newsletter.title")}
              </h2>
              <p className="mt-5 text-[#4B5563] md:text-lg leading-relaxed max-w-lg">{t("newsletter.sub")}</p>

              <form data-testid="newsletter-form" onSubmit={submit} noValidate className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("newsletter.placeholder")}
                  className="flex-1 px-5 py-4 bg-white border border-[#0A1128]/20 text-[#0A1128] placeholder:text-[#0A1128]/40 focus:border-[#D4AF37] outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={loading || done}
                  className="px-8 py-4 bg-[#D4AF37] text-[#0A1128] font-semibold hover:bg-[#E5C158] transition-colors disabled:opacity-70 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {loading ? (<><Loader2 className="animate-spin" size={18} /> {t("newsletter.subscribing")}</>)
                    : done ? (<><Check size={18} /> {t("newsletter.success").split("!")[0]}</>)
                    : t("newsletter.button")}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
